<template>
  <BaseDialog
    :visible="visible"
    @update:visible="(val) => emit('update:visible', val)"
    :width="dialogWidth"
    height="auto"
    :defaultTitleStyle="true"
    :class="[
      '!rounded-[23px] sm:!w-auto !w-[calc(100%-40px)] m-5 max-h-[90vh] overflow-y-auto',
      props.dialogClass,
    ]"
    :style="{
      margin: '20px',
      maxWidth: 'calc(100% - 40px)',
    }"
  >
    <template #header>
      <div class="flex justify-end pt-4 pb-10 w-full"></div>
    </template>
    <div class="flex flex-col items-center px-4 sm:px-8 md:px-12">
      <div
        class="w-[98px] h-[98px] md:w-[164px] md:h-[164px] gap-[16.4px] p-4 md:px-[16.4px] md:py-8 rounded-full flex items-center justify-center"
        :style="props.svgStyle"
      >
        <span
          class="flex justify-center items-center scale-75 md:scale-100"
          v-html="props.svg"
        ></span>
      </div>
      <div class="mt-6 md:mt-[52px] gap-5 text-center">
        <h2
          :class="[
            'text-btnActive items-center font-just text-[24px] md:text-[32px] leading-[100%] tracking-[0%] font-500',
            props.titleClass,
          ]"
        >
          {{ title }}
      </h2>
        <div class="mt-5 text-center">
          <p
            :class="[
              'text-btnActive font-just text-[16px] md:text-[20px] leading-[23px] tracking-[0%] font-normal',
              props.subtitleClass,
            ]"
          >
            {{ subtitle }}
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <div
        class="w-full flex items-center justify-center flex-col pb-8 px-4 sm:px-8 md:px-12 mt-8 md:mt-[52px] gap-6 mb-8"
      >
        <div class="my-2 w-full" v-if="error">
          <span
            class="text-base mb-2 !font-just text-error flex justify-center"
            data-test-id="login-errorMessage"
          >
            {{ error }}
          </span>
        </div>
        <div class="w-full flex items-center justify-center gap-6">
          <BaseButton
            ref="okButtonRef"
            :loading="loading"
            :disabled="loading"
            outlined
            @click="handleOk()"
            class="w-full rounded-full py-3 text-base !border-btnActive !border-[1px]"
          >
            <span
              class="text-btnActive font-just text-[16px] leading-[24px] tracking-[0%] font-normal"
              >{{ okBtnText }}</span
            >
          </BaseButton>
          <BaseButton
            :loading="loading"
            :disabled="loading"
            @click="handleCancel()"
            v-if="cancelBtnText"
            class="w-full rounded-full py-3 text-base !bg-btnActive"
          >
            <span
              class="!text-white font-just text-[16px] leading-[24px] tracking-[0%] font-normal"
              >{{ cancelBtnText }}</span
            >
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue";

const emit = defineEmits(["ok", "cancel", "update:visible"]);
const props = defineProps({
  visible: { type: Boolean, required: true },
  svg: {},
  title: { type: String },
  error: { type: String },
  loading: { type: Boolean },
  subtitle: { type: String },
  okBtnText: { type: String, default: "OK" },
  cancelBtnText: { type: String, default: "Cancel" },
  titleClass: { type: String, default: "" },
  subtitleClass: { type: String, default: "" },
  dialogClass: { type: String, default: "" },
  svgStyle: { type: [String, Object, Array], default: "" },
});

const windowWidth = ref(window.innerWidth);
const okButtonRef = ref(null);

const dialogWidth = computed(() => {
  return windowWidth.value < 768 ? "100%" : "55%";
});

function handleOk() {
  emit("ok");
}
function handleCancel() {
  emit("cancel");
  emit("update:visible", false);
}

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

const focusOkButton = async () => {
  await nextTick();
  if (okButtonRef.value) {
    const el = okButtonRef.value.$el || okButtonRef.value;
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }
};

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      focusOkButton();
    }
  }
);

onMounted(() => {
  window.addEventListener("resize", handleResize);
  if (props.visible) {
    focusOkButton();
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
